import {Trans} from '../../i18n/trans';
import {Link} from 'react-router-dom';
import {useAuth} from '../../auth/use-auth';
import {useIsMobileMediaQuery} from '../../utils/hooks/is-mobile-media-query';
import {
  DashboardBottomNav,
  DashboardSidebar,
} from '@app/dashboard/dashboard-sidebar';
import '@app/dashboard/dashboard-sidebar.css';
import '@app/dashboard/dashboard.css';

export function NotFoundPage() {
  const {isLoggedIn} = useAuth();
  const isMobile = useIsMobileMediaQuery();

  if (isLoggedIn) {
    return (
      <div className="db-shell">
        <DashboardSidebar activeItem="home" />
        <main className="db-main">
          <div className="dbp-root">
            <NotFoundContent isLoggedIn compact />
          </div>
        </main>
        {isMobile && <DashboardBottomNav activeTab="apps" />}
      </div>
    );
  }

  return <NotFoundContent isLoggedIn={false} compact={false} />;
}

function NotFoundContent({
  isLoggedIn,
  compact = false,
}: {
  isLoggedIn: boolean;
  compact?: boolean;
}) {
  const homeLink = isLoggedIn ? '/dashboard' : '/';

  return (
    <div className={`nfp-root${compact ? ' nfp-root--dashboard' : ''}`}>
      {/* Ambient blobs */}
      <div className="nfp-blob nfp-blob--a" aria-hidden="true" />
      <div className="nfp-blob nfp-blob--b" aria-hidden="true" />

      <div className="nfp-card">
        {/* Big 404 */}
        <div className="nfp-code" aria-hidden="true">
          <span>4</span>
          <span className="nfp-code__zero">
            {/* Animated circle with dashed ring */}
            <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="nfp-code__svg">
              <circle cx="50" cy="50" r="42" stroke="currentColor" strokeWidth="6" strokeDasharray="18 10" strokeLinecap="round" className="nfp-ring" />
              <circle cx="50" cy="50" r="26" fill="currentColor" fillOpacity=".08" />
              <text x="50" y="57" textAnchor="middle" fontSize="28" fontWeight="700" fill="currentColor" fontFamily="inherit">0</text>
            </svg>
          </span>
          <span>4</span>
        </div>

        {/* Text */}
        <h1 className="nfp-title">
          <Trans message="Page not found" />
        </h1>
        <p className="nfp-desc">
          <Trans message="The page you're looking for doesn't exist or has been moved. Let's get you back on track." />
        </p>

        {/* Actions */}
        <div className="nfp-actions">

          <button type="button" className="nfp-btn nfp-btn--ghost" onClick={() => window.history.back()}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
            <Trans message="Go back" />
          </button>
            <Link to={homeLink} className="nfp-btn nfp-btn--primary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
            {isLoggedIn ? <Trans message="Go to dashboard" /> : <Trans message="Go home" />}
          </Link>
        </div>
      </div>

      <style>{`
        .nfp-root {
          min-height: 100dvh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          background: var(--be-background, #fafafa);
          position: relative;
          overflow: hidden;
          font-family: inherit;
        }
        .nfp-root--dashboard {
          min-height: calc(100dvh - 64px);
          padding: 28px 20px 40px;
          background: transparent;
        }
        .nfp-root--dashboard .nfp-blob {
          display: none;
        }
        .nfp-root--dashboard .nfp-card {
          max-width: 560px;
          padding: 30px 28px;
          border-radius: 14px;
          background: var(--db-main-bg, #f6f5f4);
          border: 1px solid var(--db-border, rgba(0,0,0,.08));
         }
        html.dark .nfp-root,
        [data-theme='dark'] .nfp-root {
          background: var(--be-background, #0f0f10);
        }
        html.dark .nfp-root--dashboard,
        [data-theme='dark'] .nfp-root--dashboard {
          background: transparent;
        }
        html.dark .nfp-root--dashboard .nfp-card,
        [data-theme='dark'] .nfp-root--dashboard .nfp-card {
          background: var(--db-main-bg, #0a0a0a);
          border-color: var(--db-border, rgba(255,255,255,.09));
          box-shadow: none;
        }

        /* ambient blobs */
        .nfp-blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          pointer-events: none;
          opacity: .35;
        }
        html.dark .nfp-blob,
        [data-theme='dark'] .nfp-blob { opacity: .18; }

        .nfp-blob--a {
          width: 520px; height: 520px;
          top: -160px; right: -120px;
         }
        .nfp-blob--b {
          width: 360px; height: 360px;
          bottom: -100px; left: -80px;
         }

        /* card */
        .nfp-card {
          position: relative;
          z-index: 1;
          text-align: center;
          max-width: 480px;
          width: 100%;
        }

        /* 404 numeral */
        .nfp-code {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          font-size: clamp(88px, 18vw, 128px);
          font-weight: 800;
          letter-spacing: -0.04em;
          line-height: 1;
           margin-bottom: 32px;
          user-select: none;
        }


        .nfp-code__zero {
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .nfp-code__svg {
          width: 1em;
          height: 1em;
          color: inherit;
        }

        /* spinning dashed ring */
        .nfp-ring {
          transform-origin: 50px 50px;
          animation: nfp-spin 8s linear infinite;
        }
        @keyframes nfp-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }

        /* text */
        .nfp-title {
          font-size: clamp(20px, 4vw, 26px);
          font-weight: 700;
          letter-spacing: -0.02em;
          color: var(--be-foreground-base, #111);
          margin: 0 0 12px;
          line-height: 1.25;
        }
        html.dark .nfp-title,
        [data-theme='dark'] .nfp-title { color: #f4f4f5; }

        .nfp-desc {
          font-size: 15px;
          color: var(--be-foreground-muted, #6b7280);
          line-height: 1.65;
          margin: 0 auto 36px;
          max-width: 360px;
        }
        html.dark .nfp-desc,
        [data-theme='dark'] .nfp-desc { color: #9ca3af; }

        /* buttons */
        .nfp-actions {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          flex-wrap: wrap;
        }
        .nfp-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 22px;
          border-radius: 10px;
          font-size: 14.5px;
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
          border: none;
          text-decoration: none;
          transition: all .15s;
          white-space: nowrap;
        }
        .nfp-btn--primary {
          background: #000000;
          color: #fff;
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
        }
        .nfp-btn--primary:hover {
          background: #4f46e5;
          box-shadow: 0 6px 20px rgba(99, 102, 241, .45);
          transform: translateY(-1px);
        }
        .nfp-btn--ghost {
          background: transparent;
          color: var(--be-foreground-base, #374151);
          border: 1.5px solid rgba(0,0,0,.12);
        }
        html.dark .nfp-btn--ghost,
        [data-theme='dark'] .nfp-btn--ghost {
          color: #d1d5db;
          border-color: rgba(255,255,255,.12);
        }
        .nfp-btn--ghost:hover {
          background: rgba(0,0,0,.05);
          border-color: rgba(0,0,0,.22);
          transform: translateY(-1px);
        }
        html.dark .nfp-btn--ghost:hover,
        [data-theme='dark'] .nfp-btn--ghost:hover {
          background: rgba(255,255,255,.06);
          border-color: rgba(255,255,255,.22);
        }

        @media (max-width: 480px) {
          .nfp-root--dashboard {
            padding: 16px 12px 24px;
            min-height: calc(100dvh - 64px);
          }
          .nfp-root--dashboard .nfp-card {
            padding: 22px 16px;
            border-radius: 12px;
          }
          .nfp-actions { flex-direction: column; width: 100%; }
          .nfp-btn { width: 100%; justify-content: center; }
        }
      `}</style>
    </div>
  );
}
